/**
 * Onboarding contracts — slice 15, "the Reach shell": the path through an empty
 * workspace.
 *
 * Slices 0–14 built every screen a first campaign needs and never built the
 * sentence that puts them in order. A new workspace lands on an Overview with
 * nothing on it, nine destinations in the sidebar, and no indication that the
 * order is *connect a sending account → bring contacts in → build a template →
 * send yourself a test → run a campaign*. Each of those screens is good alone.
 * This file is the vocabulary of the path between them, and almost every shape
 * below exists to stop the checklist telling somebody something that is not
 * true.
 *
 * ## EXISTS, never a count — and why that is not a contradiction of slice 10
 *
 * `./overview.js` refuses contact, template and segment counts, and its header
 * is blunt about the reason: *a count that changes no decision is page-filling*.
 * That argument still holds and this file does not reopen it. The question here
 * is a different one and it does change a decision — **"do you have any
 * contacts at all"** is the difference between a step being done and not — so
 * it is asked in the form it is actually asked in: `EXISTS`, which stops at the
 * first row, and never `count(*) > 0`, which walks the table to compute a
 * number nobody wanted and then leaves that number sitting in a service where
 * somebody will eventually render it.
 *
 * The discipline is structural rather than advisory: **there is no
 * `z.number()` anywhere in this file, and no field a number could travel in.**
 * {@link OnboardingEvidence} is five booleans keyed by step id. A count cannot
 * reach the wire through this contract without somebody widening a shape, which
 * is a change a reviewer sees.
 *
 * ## Every step derives from state. Nothing here can be ticked by a person
 *
 * There is no "mark as complete", no dismissal, no stored checklist, and no
 * table behind this contract. A step is done because the thing it describes is
 * true *right now*, so a workspace that disconnects its SES credentials
 * correctly goes back to not-ready and the panel comes back.
 *
 * The rejected alternative was an `onboarding_progress` table with a row per
 * step. It is the obvious build and it drifts on the first undo: the flag says
 * "connected" and the integration says `disconnected`, and the checklist is
 * then lying about the single thing it exists to state. The same reasoning the
 * architecture rules give for computing counts and rates on read applies with
 * more force here, because a stored step is a second source of truth about
 * something a customer can change from another screen in the same session.
 *
 * What this costs: five `EXISTS` per read of the panel. That is the price and
 * it is small; what it buys is a checklist that cannot be wrong.
 *
 * ## Blocked is not "not done yet", and the shapes refuse to confuse them
 *
 * You cannot send a test without a template. That step is not merely undone —
 * it is **blocked**, and the two prompt completely different actions: one says
 * *do this now*, the other says *this is waiting on something else*. Rendering
 * them the same way sends somebody to a screen that will refuse them.
 *
 * So {@link onboardingStepSchema} is a discriminated union with three arms and
 * **no boolean anywhere in it**:
 *
 *   - `done` — carries nothing else. There is nothing to say.
 *   - `ready` — carries nothing else. It is `.strict()`, so a `blockedBy` on a
 *     ready step is a parse error rather than a field a component might read.
 *   - `blocked` — **must** name what blocks it, as a non-empty list of step
 *     ids. A blocked step with nothing blocking it is not constructible in
 *     TypeScript and not parseable from JSON.
 *
 * There is deliberately no flat `ONBOARDING_STEP_STATUS_VALUES` enum beside the
 * union. A status list that can gain a member independently of the union is
 * exactly the drift this file is built to prevent, and the union is already
 * exhaustive for a `switch`. And because no arm carries a boolean, `!done`
 * cannot be computed and the two non-done states cannot be collapsed into one
 * by a component author in a hurry. This is the same type-level discipline
 * slice 11 used to keep a directional engagement rate out of a KPI tile and
 * slice 14 used to keep a row count from being printed as reach.
 *
 * ## The order is load-bearing, and one invariant makes it safe
 *
 * {@link ONBOARDING_PREREQUISITES} names, per step, the steps that must be done
 * before it is possible. **Every prerequisite is earlier in
 * {@link ONBOARDING_STEP_VALUES} than the step it blocks**, and that gives a
 * property worth stating: *the first step that is not done is always `ready`* —
 * if everything before it is done then none of its prerequisites is unmet. The
 * panel therefore always has exactly one obvious thing to do, and never opens
 * on a blocked step with no way forward.
 *
 * That invariant is not enforceable in the type system, so
 * {@link onboardingResponseSchema} enforces it at parse time and says so in the
 * failure message. A future step whose prerequisite sits later in the order
 * would fail the API's parse of its own response, which is where this codebase
 * prefers to find that class of mistake.
 *
 * `run_campaign` pointedly does **not** list `send_test` as a prerequisite. The
 * send path does not require a test send and slice 15 does not add a gate, so
 * claiming a block the product does not enforce would teach people within a day
 * that the checklist is decorative. Test-sending first is a habit worth
 * building — it is what stands between 1,287 real cooperative-bank addresses
 * and a first cold send — and slice 18 is where a real list-safety gate belongs.
 * Until then it is a step in the order, not a lock.
 *
 * Segments are not a sixth step. Choosing an audience is part of composing a
 * campaign, and a checklist that grows an entry per screen is the sidebar
 * problem restated inside a panel.
 *
 * ## What each step actually reads
 *
 * Five `EXISTS`, each scoped by RLS, each named here so the API and the verify
 * suite cannot disagree about what "done" means:
 *
 *   1. `connect_sending_account` — an `integrations` row with `kind = 'ses'` and
 *      `status IN ('connected','expiring')`. `expiring` counts: it is a working
 *      credential with a deadline, and telling somebody they have not connected
 *      an account they are actively sending on is false. `failed` and
 *      `disconnected` do not count.
 *
 *      This step says an account is **connected**. It deliberately does not say
 *      the account can reach anybody — sandbox state, verified identities and
 *      quota are `./overview.js`'s `sendability`, which is a live-ish verdict
 *      the Overview already renders. Two surfaces computing "can you send"
 *      separately is how they end up disagreeing, and the repair for a sandboxed
 *      account is on the Integrations screen the step already points at.
 *
 *   2. `import_contacts` — a `contacts` row. **Not a `datasets` row**: a dataset
 *      exists the moment a file is uploaded and before a single row is
 *      imported, so a checklist reading `datasets` ticks for an import that
 *      rejected everything and tells somebody they have an audience they do not
 *      have.
 *
 *      Not narrowed to contacts carrying an email address either, tempting as
 *      that is on an email-shaped path. Somebody who has just uploaded a
 *      phone-only list and is told they have no contacts goes looking for a
 *      broken import. Whether a list is *mailable* is a segment's question and
 *      slice 14's `Reach` is the unit that answers it.
 *
 *   3. `build_template` — a `templates` row with `type = 'email'`. Narrow on
 *      purpose: the arrow from this step to `send_test` is only true for an
 *      email template, because test-sending reads the SES account. A checklist
 *      whose "blocked, you need a template" points at a WhatsApp template the
 *      test-send screen will refuse is worse than one that is narrow and right.
 *
 *   4. `send_test` — an `audit_log` row with
 *      `action = ONBOARDING_TEST_SEND_AUDIT_ACTION`. See the next section; this
 *      is the one step whose evidence slice 15 has to create.
 *
 *   5. `run_campaign` — a `messages` row. Not a subset of
 *      `CAMPAIGN_STATUS_VALUES`: the statuses admit at least three defensible
 *      readings of "ran" (does `cancelled` count when it was cancelled
 *      mid-send? does `failed` count when 900 went out first?), and any subset
 *      chosen today is a second definition of the word that drifts the day
 *      another status is added. **That day arrived**: slice 18 appended
 *      `holding_between_waves` and `halted_delivery_risk`, both of which a
 *      status-subset reading would have had to take a position on and neither of
 *      which changes the answer here. A `messages` row exists because the runner
 *      materialised and attempted a send. That is the fact, and it survives
 *      every status the campaign later moves through.
 *
 * ## Three rules for the reads, none of which this file can enforce
 *
 * **All five `EXISTS` go in one `TenantDbService.run()` callback.** `run()`
 * commits when its callback returns, so five separate calls are five different
 * instants, and a checklist assembled from five instants can describe a
 * workspace that never existed — contacts imported between the second read and
 * the fifth make a step both done and still blocking. One callback, one
 * snapshot, five cheap probes.
 *
 * **No `tenant_id` in any of the five `WHERE` clauses.** RLS does it. Writing it
 * as well is the bug rather than the safety net, exactly as it is everywhere
 * else in this codebase.
 *
 * **A read that fails is not a `false`.** "We could not find out" and "there is
 * nothing there" are different statements and this contract can only carry the
 * second, deliberately — an error arm on a five-boolean response is a state
 * every consumer would have to handle and most would render as an empty
 * checklist anyway. So a failing probe must fail the endpoint, and the panel
 * shows the screen's error state instead of a confident list of things you have
 * not done. Defaulting a failed read to `false` would put this file in the
 * family of defects it was written to avoid: an absence rendered as a fact.
 *
 * ## The hole this leaves: a WhatsApp-only workspace
 *
 * The five reads are the **email path**. Step 1 is an SES connection, step 3 is
 * an email template, and step 4 is a test send that only exists for email —
 * `test-send.service.ts` reads the SES account and there is no WhatsApp
 * equivalent to point at. A workspace that connects only Meta therefore never
 * finishes this checklist, and the panel does not go away.
 *
 * That is a real wart and it is left deliberately rather than papered over.
 * Widening step 1 to "any connected integration" would tick it for a Meta
 * connection and then send that person to a test-send screen that refuses them
 * for want of SES — a blocked arrow pointing at the wrong repair, which is
 * worse than a checklist that is narrow and correct. SLICES is explicit that
 * Reach ships email-first and slice 19's first real send is SES; when WhatsApp
 * becomes a self-serve path it needs a test-send screen before it needs a
 * checklist, and this vocabulary gains a channel then.
 *
 * The consequence for copy, which the web app owns: **step 1's label names
 * Amazon SES.** The step id is channel-neutral because ids should not churn,
 * but a label reading "connect a sending account" beside a read that only sees
 * SES is the lie this note exists to prevent.
 *
 * ## The test send left no trace, and that is why this file names an audit action
 *
 * `test-send.service.ts` used to assemble a message, hand it to the transport
 * and return, writing **nothing**: no `messages` row (that table's `campaign_id`
 * is `NOT NULL` behind a composite FK, and a test send has no campaign), no
 * `campaign_recipients` row, no event. The only trace was a log line. So on the
 * day this contract was written, "have you sent yourself a test" was not
 * derivable from state at all — and a step that can never be done is a panel
 * that never disappears, which is the failure mode decision 6 exists to avoid.
 *
 * The fix is one row in `audit_log`, and that table is the right home rather
 * than a new one: it is already tenant-scoped under RLS with `USING` and
 * `WITH CHECK`, already append-only for the application role, already the place
 * a dotted verb about *a person doing something in a workspace* goes, and
 * `writeAuditEntry` was explicitly built to be borrowed. A test send is a user
 * action, not a message entity — there is no message — so a `test_sends` table
 * would be a new table holding one fact that this one already holds a shape for.
 *
 * {@link ONBOARDING_TEST_SEND_AUDIT_ACTION} exists because the writer and the
 * reader are in different modules and the failure mode of them disagreeing is
 * silent: the write says `campaign.test_send`, a hand-typed read asks for
 * `test_send`, nothing throws, and the step simply never ticks for anybody
 * ever. One constant, imported by both sides, and a typo stops being possible.
 *
 * Two rules for that write, both about the same 1,287 real addresses. Both are
 * now discharged by `TestSendService.recordTestSend`, and both are stated here
 * rather than only there because this is the file the next channel's test send
 * will be written against:
 *
 *   - **The audit row records that a test was sent, and nothing about who to.**
 *     No recipient in `metadata`, not masked, not hashed. `audit_log` has no
 *     retention policy and is readable by staff on the `BYPASSRLS` connection,
 *     so a recipient in metadata creates a new permanent home for a real
 *     address. The template id is enough to be useful, and the writer is not
 *     handed the address at all, so the rule is a fact about its signature
 *     rather than a note somebody has to remember.
 *   - **The write happens after the provider accepted, and its failure must not
 *     become the caller's error.** The mail is already gone; turning a
 *     successful send into a 500 because an audit insert failed would be the
 *     worst possible trade. The step stays unticked, the person sends another
 *     test, and nothing is lost.
 *
 * ## It disappears when it is done, and the contract is what makes that true
 *
 * {@link onboardingResponseSchema} is a union on `setup`, and the `complete`
 * arm **carries no steps at all**. A finished workspace cannot render a
 * checklist of five ticks, because the response has nothing to render. That is
 * decision 6 made structural instead of left to a `hidden` prop that somebody
 * removes while debugging.
 *
 * What replaces it: nothing. The Overview goes back to being the Overview, and
 * every fact the checklist derived from is still visible on the screen that
 * owns it — the connection on Integrations, the contacts on Audiences, the
 * campaign in the recent-campaigns list. Nothing is lost by the panel leaving,
 * which is the test of whether it should stay. It returns on its own if a step
 * stops being true, because nothing was ever stored.
 *
 * The cost of the union, stated plainly: a `complete` response carries no
 * evidence, so a service that computed it wrongly cannot be caught by parsing.
 * That is what {@link onboardingResponse} is for — both arms come from one
 * derivation over one evidence object — and what the verify suite asserts
 * against seeded tenants.
 *
 * ## No `generatedAt`
 *
 * Every other response of this size carries one. This one does not, and the
 * absence is deliberate: `./overview.js` timestamps itself because its numbers
 * are a *window* — "3 bounces" means nothing without saying over what — and a
 * screen that refreshes half a snapshot invites comparing two instants. There
 * is no window here and no arithmetic. Five facts are true now or they are not,
 * and a timestamp nobody renders is the page-filling this shell is meant to
 * remove.
 *
 * ## Position in the package
 *
 * A **leaf**. It imports nothing from `./` and only `../common.js` would be a
 * candidate — and it does not need that either, having no timestamp and no id.
 * Nothing imports it. Slices 10, 12, 8 and 13 all record the same trap: two
 * modules that both build zod schemas at load and import each other fail at
 * *import* with `z.enum(undefined)`, not at review. The cheapest way to stay out
 * of it is to have no edges at all, and this file has none.
 *
 * Copy is not here. The words on the panel are the web app's, per the text rule
 * this slice runs on — short by default, detail on demand — and step ids are an
 * enum precisely so the label map can be `Record<OnboardingStepId, …>` and fail
 * to compile the day a sixth step arrives without words.
 */
import { z } from 'zod';

/* -------------------------------------------------------------------------- */
/* The steps                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The five steps, **in the order they must be done**.
 *
 * The array order is the render order and the derivation order; it is not
 * alphabetical and must not be sorted. `onboardingResponse` walks it, the
 * response schema refuses steps that arrive in any other order, and the "first
 * step that is not done is always ready" invariant is a property of this order
 * together with {@link ONBOARDING_PREREQUISITES}.
 *
 * The ids are channel-neutral; the five reads behind them are not. The header
 * names the exact `EXISTS` for each — read it before writing a query, and
 * before writing a label.
 */
export const ONBOARDING_STEP_VALUES = [
  'connect_sending_account',
  'import_contacts',
  'build_template',
  'send_test',
  'run_campaign',
] as const;
export const onboardingStepIdSchema = z.enum(ONBOARDING_STEP_VALUES);
export type OnboardingStepId = z.infer<typeof onboardingStepIdSchema>;

/**
 * What must be done before a step is possible at all.
 *
 * These are hard blocks — the screen behind the step will refuse without them —
 * not recommendations. `send_test` needs an account to send through and a
 * template to send; `run_campaign` needs those plus somebody to send to.
 *
 * `send_test` is **not** listed under `run_campaign`: nothing in the send path
 * requires it, and the checklist does not invent a gate the product does not
 * have. See the header.
 *
 * INVARIANT: every prerequisite is earlier in {@link ONBOARDING_STEP_VALUES}
 * than the step that lists it. {@link onboardingResponseSchema} fails to parse
 * if that stops being true, because the panel would then be able to open on a
 * blocked step with nothing actionable above it.
 */
export const ONBOARDING_PREREQUISITES: Readonly<
  Record<OnboardingStepId, readonly OnboardingStepId[]>
> = {
  connect_sending_account: [],
  import_contacts: [],
  build_template: [],
  send_test: ['connect_sending_account', 'build_template'],
  run_campaign: ['connect_sending_account', 'import_contacts', 'build_template'],
};

/**
 * The `audit_log.action` a test send writes, and the one the `send_test` step
 * reads back.
 *
 * Both halves import this. Two hand-typed strings in two modules that must
 * match, where a mismatch throws nothing and merely leaves a checklist step
 * unticked forever, is the exact defect this constant is here to make
 * impossible.
 *
 * Dotted verb, matching the slice-0 vocabulary (`auth.login`,
 * `integration.connected`). The audit row's `entity_type` is the template and
 * its `entity_id` is the template id; **its metadata carries no recipient** —
 * see the header.
 */
export const ONBOARDING_TEST_SEND_AUDIT_ACTION = 'campaign.test_send';

/* -------------------------------------------------------------------------- */
/* A step's state                                                              */
/* -------------------------------------------------------------------------- */

/**
 * One step, as the panel receives it.
 *
 * Three arms, no boolean, and `blocked` is the only one that carries anything:
 *
 *   - `done` — the state behind it is true right now.
 *   - `ready` — not done, and nothing is in the way. Do this next.
 *   - `blocked` — not done, and it cannot be done yet. `blockedBy` names the
 *     steps that are missing, is non-empty by construction, and lists only the
 *     prerequisites that are *actually* unmet, so a panel says "needs a
 *     template" rather than reciting the full prerequisite list.
 *
 * `.strict()` on every arm is doing work: it makes `blockedBy` on a `ready`
 * step a parse failure rather than a stray property a component might read, and
 * it means a service that tried to send a fourth, softer state would fail
 * parsing its own response.
 */
export const onboardingStepSchema = z.discriminatedUnion('status', [
  z.object({ id: onboardingStepIdSchema, status: z.literal('done') }).strict(),
  z.object({ id: onboardingStepIdSchema, status: z.literal('ready') }).strict(),
  z
    .object({
      id: onboardingStepIdSchema,
      status: z.literal('blocked'),
      /** The unmet prerequisites, in {@link ONBOARDING_STEP_VALUES} order. */
      blockedBy: z.array(onboardingStepIdSchema).nonempty(),
    })
    .strict(),
]);
export type OnboardingStep = z.infer<typeof onboardingStepSchema>;

/* -------------------------------------------------------------------------- */
/* Evidence                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The answer to each step's `EXISTS`, and the whole input to the derivation.
 *
 * One boolean per step id, so adding a step breaks every construction site
 * rather than defaulting the new one to `false` somewhere quiet. Each value
 * answers **is there any**, never **how many** — the header names the exact
 * read for each, and there is nowhere in this file for a count to live.
 *
 * This type never crosses the wire. It is the API's intermediate between five
 * queries and one response, and it is exported so the verify suite can build
 * one by hand and assert the derivation without going near the database.
 */
export type OnboardingEvidence = Readonly<Record<OnboardingStepId, boolean>>;

function stepFrom(id: OnboardingStepId, evidence: OnboardingEvidence): OnboardingStep {
  if (evidence[id]) return { id, status: 'done' };

  // Only the prerequisites that are genuinely missing. The destructure is what
  // proves the list is non-empty to the type, rather than a `!` or a cast.
  const [first, ...rest] = ONBOARDING_PREREQUISITES[id].filter((prerequisite) => {
    return !evidence[prerequisite];
  });

  return first === undefined
    ? { id, status: 'ready' }
    : { id, status: 'blocked', blockedBy: [first, ...rest] };
}

/**
 * Derives all five steps, in order, from the five facts.
 *
 * The single definition of what `done`, `ready` and `blocked` mean. The API does
 * not compute states beside its queries and the web app does not compute them
 * beside its rendering: both would be a second opinion about the word
 * "blocked", and the two would drift in the direction that stops checking.
 */
export function onboardingSteps(evidence: OnboardingEvidence): readonly OnboardingStep[] {
  return ONBOARDING_STEP_VALUES.map((id) => stepFrom(id, evidence));
}

/* -------------------------------------------------------------------------- */
/* The response                                                                */
/* -------------------------------------------------------------------------- */

/**
 * `GET /onboarding`. Either there is a path left to walk, or there is not.
 *
 * The `complete` arm carries no steps, which is the point: a workspace that has
 * finished cannot be handed a checklist to render. See the header for what
 * replaces the panel — nothing — and why that is the right answer.
 *
 * On the `in_progress` arm, `steps` carries **all five** including the done
 * ones, because a checklist showing only what is left loses the sense of
 * progress that makes it worth reading; `next` is the one thing to do now, and
 * the refinement below pins it to the first step that is not done and insists
 * that step is `ready`.
 *
 * `next` is redundant with `steps` and is carried anyway. A single field the
 * panel keys its call-to-action off beats every consumer re-deriving "the first
 * one that is not done", and the refinement makes the two unable to disagree:
 * a response where they do disagree does not parse.
 */
export const onboardingResponseSchema = z
  .discriminatedUnion('setup', [
    z.object({ setup: z.literal('complete') }).strict(),
    z
      .object({
        setup: z.literal('in_progress'),
        /** Every step, in {@link ONBOARDING_STEP_VALUES} order. */
        steps: z.array(onboardingStepSchema),
        /** The first step that is not done. Always a `ready` one — see below. */
        next: onboardingStepIdSchema,
      })
      .strict(),
  ])
  .superRefine((value, ctx) => {
    if (value.setup !== 'in_progress') return;

    const ids = value.steps.map((step) => step.id);
    const inOrder =
      ids.length === ONBOARDING_STEP_VALUES.length &&
      ONBOARDING_STEP_VALUES.every((id, index) => ids[index] === id);

    if (!inOrder) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['steps'],
        message:
          'steps must carry every onboarding step exactly once, in ONBOARDING_STEP_VALUES order — the order is the path, not a caller preference',
      });
      return;
    }

    const first = value.steps.find((step) => step.status !== 'done');

    if (first === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['setup'],
        message: 'every step is done; this response must use the "complete" arm and carry no steps',
      });
      return;
    }

    if (first.id !== value.next) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['next'],
        message: `next must be the first step that is not done (${first.id})`,
      });
    }

    if (first.status !== 'ready') {
      // Only reachable if a prerequisite sits later in the order than the step
      // it blocks. See the invariant on ONBOARDING_PREREQUISITES.
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['steps'],
        message: `the first step that is not done (${first.id}) is blocked, which means a prerequisite is later in the order than the step it blocks — the panel would open with nothing to do`,
      });
    }
  });
export type OnboardingResponse = z.infer<typeof onboardingResponseSchema>;

/**
 * Builds the response from the evidence. The only place either arm is chosen.
 *
 * The API composes it from five `EXISTS` results and returns it; the web app
 * renders it and derives nothing. Keeping the arm choice here is what stops a
 * `complete` response ever being returned beside an unfinished checklist —
 * there is one comparison, in one function, and both callers use it.
 */
export function onboardingResponse(evidence: OnboardingEvidence): OnboardingResponse {
  const steps = onboardingSteps(evidence);
  const next = steps.find((step) => step.status !== 'done');

  if (next === undefined) return { setup: 'complete' };
  return { setup: 'in_progress', steps: [...steps], next: next.id };
}
